import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import './ContactedPrecatoriosList.css';
import { getToken } from '../../../../services/api';
import { taxaSelicService } from '../../../../services/authService';
import { useAuth } from '../../../../context/AuthContext';
import { MdArrowBack, MdComment, MdEdit, MdPerson, MdCalendarToday, MdAttachMoney, MdGavel, MdContactPhone, MdDelete, MdBlock, MdFilterList, MdClose, MdHistory, MdStorage } from 'react-icons/md';
import JurisConsultModal from './components/JurisConsultModal';
import { FaUser, FaBuilding } from 'react-icons/fa';
import EditContactModal from './components/EditContactModal';
import DocumentChecklistModal from './components/DocumentChecklistModal';
import ProposalModal from './components/ProposalModal';
import EditCredorModal from './components/EditCredorModal';

const ContactedPrecatoriosList = () => {
  const { state } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [precatorios, setPrecatorios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedPrecatorio, setSelectedPrecatorio] = useState(null);
  const [showCommentModal, setShowCommentModal] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showEditContactModal, setShowEditContactModal] = useState(false);
  const [taxasData, setTaxasData] = useState([]);
  const [selectedPrecatorioForContact, setSelectedPrecatorioForContact] = useState(null);
  const [showChecklistModal, setShowChecklistModal] = useState(false);
  const [showProposalModal, setShowProposalModal] = useState(false);
  const [showAllCommentsModal, setShowAllCommentsModal] = useState(false);
  const [selectedPrecatorioComments, setSelectedPrecatorioComments] = useState(null);
  const [newCommentInModal, setNewCommentInModal] = useState('');
  const [comment, setComment] = useState('');
  const [newStatus, setNewStatus] = useState('');
  const [statusMotivo, setStatusMotivo] = useState('');
  // Comment edit/delete states
  const [editingComment, setEditingComment] = useState(null);
  const [editCommentText, setEditCommentText] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [commentToDelete, setCommentToDelete] = useState(null);
  // Mark as bad states
  const [showMarkAsBadModal, setShowMarkAsBadModal] = useState(false);
  const [badMotivo, setBadMotivo] = useState('');
  const [badMotivoDescricao, setBadMotivoDescricao] = useState('');
  // Edit credor info states
  const [showEditCredorModal, setShowEditCredorModal] = useState(false);
  const [editingCredorPrecatorio, setEditingCredorPrecatorio] = useState(null);
  // Audit log modal states
  const [showAuditLogModal, setShowAuditLogModal] = useState(false);
  const [showJurisModal, setShowJurisModal] = useState(false);
  const [selectedPrecatorioJuris, setSelectedPrecatorioJuris] = useState(null);
  const [jurisConsultData, setJurisConsultData] = useState(null);
  const [loadingJuris, setLoadingJuris] = useState(false);
  const [selectedPrecatorioAuditLog, setSelectedPrecatorioAuditLog] = useState(null);

  // Filter states
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    nome: '',
    numero_precatorio: '',
    documento: '',
    valorMin: '',
    valorMax: '',
    natureza: '',
    anoPrevisto: ''
  });

  // Manager roles that can edit/delete any comment
  const MANAGER_ROLES = ['admin', 'gestor_comercial', 'diretoria_comercial'];

  // Check if current user can modify a comment
  const canModifyComment = (comment) => {
    if (!user || !comment.usuario) return false;
    const isAuthor = comment.usuario._id?.toString() === user._id?.toString();
    const isManager = MANAGER_ROLES.includes(user.role);
    const isSystemComment = comment.tipo === 'sistema';
    const isDeleted = comment.excluido;
    return (isAuthor || isManager) && !isSystemComment && !isDeleted;
  };

  const stateNames = {
    'BA': 'Bahia',
    'MA': 'Maranhão',
    'RN': 'Rio Grande do Norte',
    'PB': 'Paraíba',
    'RS': 'Rio Grande do Sul',
    'PR': 'Paraná',
    'SP': 'São Paulo',
    'RJ': 'Rio de Janeiro',
    'MG': 'Minas Gerais',
    'MT': 'Mato Grosso',
    'MS': 'Mato Grosso do Sul'
  };

  const statusOptions = [
    { value: 'novo', label: 'Novo', color: '#1976d2' },
    { value: 'solicitacao_proposta', label: 'Solicitação de Proposta', color: '#7b1fa2' },
    { value: 'proposta_aceita', label: 'Proposta Aceita', color: '#388e3c' },
    { value: 'proposta_negada', label: 'Proposta Negada', color: '#c62828' },
    { value: 'solicitacao_documentos', label: 'Solicitação de Documentos', color: '#f57c00' },
    { value: 'documentos_completos', label: 'Documentos Completos', color: '#fbc02d' },
    { value: 'em_analise', label: 'Em Análise', color: '#0288d1' },
    { value: 'fechado', label: 'Fechado', color: '#2e7d32' }
  ];

  const badMotivoOptions = [
    { value: 'falecido', label: 'Credor falecido' },
    { value: 'dados_incorretos', label: 'Dados incorretos' },
    { value: 'nao_interessado', label: 'Não tem interesse' },
    { value: 'ja_vendeu', label: 'Já vendeu o precatório' },
    { value: 'fraude', label: 'Suspeita de fraude' },
    { value: 'outro', label: 'Outro motivo' }
  ];

  // Filter logic
  const filteredPrecatorios = useMemo(() => {
    return precatorios.filter(p => {
      // Nome filter (case-insensitive partial match)
      if (filters.nome && !p.nome?.toLowerCase().includes(filters.nome.toLowerCase())) return false;

      // Numero precatório filter (partial match)
      if (filters.numero_precatorio && !p.numero_precatorio?.toLowerCase().includes(filters.numero_precatorio.toLowerCase())) return false;

      // Documento filter (CPF/OAB/CNPJ - partial match)
      if (filters.documento) {
        const docMatch = p.cpf_cnpj?.includes(filters.documento) || p.document_used?.includes(filters.documento);
        if (!docMatch) return false;
      }

      // Value range filter
      const valor = p.valor_atualizado || 0;
      if (filters.valorMin && valor < parseFloat(filters.valorMin)) return false;
      if (filters.valorMax && valor > parseFloat(filters.valorMax)) return false;

      // Natureza filter
      if (filters.natureza && p.natureza !== filters.natureza) return false;

      // Ano previsto filter
      if (filters.anoPrevisto && p.ano_pagamento_previsto?.toString() !== filters.anoPrevisto) return false;

      return true;
    });
  }, [precatorios, filters]);

  // Get unique anos for filter dropdown
  const anosPrevistos = useMemo(() => {
    const anos = [...new Set(precatorios.map(p => p.ano_pagamento_previsto).filter(Boolean))];
    return anos.sort((a, b) => a - b);
  }, [precatorios]);

  // Clear all filters
  const clearFilters = () => {
    setFilters({
      nome: '',
      numero_precatorio: '',
      documento: '',
      valorMin: '',
      valorMax: '',
      natureza: '',
      anoPrevisto: ''
    });
  };

  // Handle filter change
  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  // Check if any filter is active
  const hasActiveFilters = Object.values(filters).some(v => v !== '');

  useEffect(() => {
    fetchPrecatorios();
  }, [state]);

  useEffect(() => {
    fetchTaxas();
  }, []);

  const fetchTaxas = async () => {
    try {
      const response = await taxaSelicService.getAll();
      if (response.success) {
        setTaxasData(response.data || []);
      }
    } catch (error) {
      console.error('Error fetching taxas:', error);
    }
  };

  const calculateUpdatedValue = (precatorio) => {
    // Check if all required fields exist
    if (!precatorio.orcamento || !precatorio.ano_pagamento_previsto || !precatorio.valor_atualizado) {
      return null;
    }

    // Parse years
    const startYear = parseInt(precatorio.orcamento);
    const endYear = parseInt(precatorio.ano_pagamento_previsto);

    // Validate years
    if (isNaN(startYear) || isNaN(endYear) || startYear >= endYear) {
      return null;
    }

    // Start with valor_atualizado
    let valor = precatorio.valor_atualizado;

    // Apply compound interest year by year
    for (let year = startYear; year <= endYear; year++) {
      const taxaEntry = taxasData.find(t => t.ano === year);
      if (!taxaEntry) {
        // Missing rate for this year, don't show calculated value
        return null;
      }
      // Apply compound interest: valor * (1 + taxa)
      valor = valor * (1 + taxaEntry.taxa);
    }

    return valor;
  };

  const calculateCompoundInterestFactor = (anoPagamentoPrevisto) => {
    if (!anoPagamentoPrevisto) return null;

    const currentYear = new Date().getFullYear();
    const endYear = parseInt(anoPagamentoPrevisto);

    if (isNaN(endYear) || currentYear > endYear) {
      return null;
    }

    const years = endYear - currentYear;
    const fixedRate = 0.06; // 6% per year
    const factor = Math.pow(1 + fixedRate, years);

    return factor;
  };

  const fetchPrecatorios = async () => {
    try {
      setLoading(true);
      const token = getToken();
      const API_URL = process.env.NODE_ENV === 'production' 
        ? process.env.REACT_APP_VERCEL_API_URL 
        : process.env.REACT_APP_LOCAL_API_URL;

      const response = await fetch(`${API_URL}/contacted-precatorios/${state}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Erro ao buscar precatórios');
      }

      const data = await response.json();
      setPrecatorios(data.data || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAddComment = async () => {
    if (!comment.trim() || !selectedPrecatorio) return;

    try {
      const token = getToken();
      const API_URL = process.env.NODE_ENV === 'production' 
        ? process.env.REACT_APP_VERCEL_API_URL 
        : process.env.REACT_APP_LOCAL_API_URL;

      const response = await fetch(`${API_URL}/contacted-precatorios/${state}/${selectedPrecatorio._id}/comment`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          texto: comment,
          tipo: 'observacao'
        })
      });

      if (!response.ok) {
        throw new Error('Erro ao adicionar comentário');
      }

      // Refresh the list
      fetchPrecatorios();
      setShowCommentModal(false);
      setComment('');
      setSelectedPrecatorio(null);
    } catch (err) {
      alert('Erro ao adicionar comentário: ' + err.message);
    }
  };

  const handleAddCommentFromModal = async () => {
    if (!newCommentInModal.trim() || !selectedPrecatorioComments) return;

    try {
      const token = getToken();
      const API_URL = process.env.NODE_ENV === 'production'
        ? process.env.REACT_APP_VERCEL_API_URL
        : process.env.REACT_APP_LOCAL_API_URL;

      const response = await fetch(`${API_URL}/contacted-precatorios/${state}/${selectedPrecatorioComments._id}/comment`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          texto: newCommentInModal,
          tipo: 'observacao'
        })
      });

      if (!response.ok) {
        throw new Error('Erro ao adicionar comentário');
      }

      const updatedData = await response.json();

      // Update the selected precatorio with new comments
      if (updatedData.data) {
        setSelectedPrecatorioComments(updatedData.data);
      }

      // Refresh the list
      fetchPrecatorios();
      setNewCommentInModal('');
    } catch (err) {
      alert('Erro ao adicionar comentário: ' + err.message);
    }
  };

  // Handle edit comment
  const handleEditComment = async () => {
    if (!editingComment || !editCommentText.trim()) return;

    try {
      const token = getToken();
      const API_URL = process.env.NODE_ENV === 'production'
        ? process.env.REACT_APP_VERCEL_API_URL
        : process.env.REACT_APP_LOCAL_API_URL;

      const response = await fetch(
        `${API_URL}/contacted-precatorios/${state}/${selectedPrecatorioComments._id}/comment/${editingComment._id}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ texto: editCommentText.trim() })
        }
      );

      const data = await response.json();

      if (data.success) {
        // Update the comments in modal
        setSelectedPrecatorioComments(data.data);
        // Refresh precatorios list
        fetchPrecatorios();
        // Reset edit state
        setEditingComment(null);
        setEditCommentText('');
      } else {
        alert(data.message || 'Erro ao editar comentário');
      }
    } catch (error) {
      console.error('Error editing comment:', error);
      alert('Erro ao editar comentário');
    }
  };

  // Handle delete comment
  const handleDeleteComment = async () => {
    if (!commentToDelete) return;

    try {
      const token = getToken();
      const API_URL = process.env.NODE_ENV === 'production'
        ? process.env.REACT_APP_VERCEL_API_URL
        : process.env.REACT_APP_LOCAL_API_URL;

      const response = await fetch(
        `${API_URL}/contacted-precatorios/${state}/${selectedPrecatorioComments._id}/comment/${commentToDelete._id}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      const data = await response.json();

      if (data.success) {
        // Update the comments in modal
        setSelectedPrecatorioComments(data.data);
        // Refresh precatorios list
        fetchPrecatorios();
        // Reset delete state
        setShowDeleteConfirm(false);
        setCommentToDelete(null);
      } else {
        alert(data.message || 'Erro ao excluir comentário');
      }
    } catch (error) {
      console.error('Error deleting comment:', error);
      alert('Erro ao excluir comentário');
    }
  };

  // Handle marking precatorio as bad
  const handleMarkAsBad = async () => {
    if (!badMotivo || !selectedPrecatorio) return;
    if (badMotivo === 'outro' && !badMotivoDescricao.trim()) {
      alert('Por favor, descreva o motivo');
      return;
    }

    try {
      const token = getToken();
      const API_URL = process.env.NODE_ENV === 'production'
        ? process.env.REACT_APP_VERCEL_API_URL
        : process.env.REACT_APP_LOCAL_API_URL;

      const response = await fetch(`${API_URL}/contacted-precatorios/${state}/${selectedPrecatorio._id}/mark-as-bad`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          motivo: badMotivo,
          motivo_descricao: badMotivoDescricao.trim()
        })
      });

      const data = await response.json();

      if (data.success) {
        // Refresh the list
        fetchPrecatorios();
        setShowMarkAsBadModal(false);
        setBadMotivo('');
        setBadMotivoDescricao('');
        setSelectedPrecatorio(null);
        alert('Precatório arquivado provisoriamente');
      } else {
        alert(data.message || 'Erro ao arquivar precatório');
      }
    } catch (error) {
      console.error('Error marking as bad:', error);
      alert('Erro ao arquivar precatório');
    }
  };

  // Format comment display with edit/delete status
const handleOpenJurisConsult = async (precatorio) => {
    setSelectedPrecatorioJuris(precatorio);
    setShowJurisModal(true);
    setLoadingJuris(true);
    setJurisConsultData(null);

    try {
      const token = getToken();
      const API_URL = process.env.NODE_ENV === 'production'
        ? process.env.REACT_APP_VERCEL_API_URL
        : process.env.REACT_APP_LOCAL_API_URL;

      const response = await fetch(`${API_URL}/contacted-precatorios/${state}/${precatorio._id}/jurisconsult`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      if (data.success) {
        setJurisConsultData(data.data);
      }
    } catch (error) {
      console.error('Error fetching JurisConsult data:', error);
    } finally {
      setLoadingJuris(false);
    }
  };

    const formatCommentStatus = (comment) => {
    if (comment.excluido) {
      const deletedByName = comment.excluido_por?.name || 'Usuário';
      const deletedDate = formatDate(comment.data_exclusao);
      return `(excluído por ${deletedByName} em ${deletedDate})`;
    }
    if (comment.editado) {
      return '(editado)';
    }
    return null;
  };

  const handleUpdateStatus = async () => {
    if (!newStatus || !selectedPrecatorio) return;

    try {
      const token = getToken();
      const API_URL = process.env.NODE_ENV === 'production' 
        ? process.env.REACT_APP_VERCEL_API_URL 
        : process.env.REACT_APP_LOCAL_API_URL;

      const response = await fetch(`${API_URL}/contacted-precatorios/${state}/${selectedPrecatorio._id}/status`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          status: newStatus,
          motivo: statusMotivo
        })
      });

      if (!response.ok) {
        throw new Error('Erro ao atualizar status');
      }

      // Refresh the list
      fetchPrecatorios();
      setShowStatusModal(false);
      setNewStatus('');
      setStatusMotivo('');
      setSelectedPrecatorio(null);
    } catch (err) {
      alert('Erro ao atualizar status: ' + err.message);
    }
  };

  const formatCurrency = (value) => {
    if (!value) return 'R$ 0,00';
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const formatNatureza = (natureza) => {
    if (!natureza) return '-';
    
    const naturezaMap = {
      'C': 'Comum',
      'A': 'Alimentar',
      'P': 'Prioritário'
    };
    
    return naturezaMap[natureza.toUpperCase()] || natureza;
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('pt-BR');
  };

  const getStatusBadge = (status) => {
    const option = statusOptions.find(opt => opt.value === status);
    if (!option) return { label: status, color: '#6c757d' };
    return option;
  };

  const formatDocument = (doc, type) => {
    if (!doc) return '-';
    const cleaned = doc.replace(/\D/g, '');
    
    if (type === 'CPF' && cleaned.length === 11) {
      return cleaned.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
    } else if (type === 'CNPJ' && cleaned.length === 14) {
      return cleaned.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
    } else if (type === 'OAB') {
      return doc; // OAB já vem formatado
    }
    return doc;
  };

  const getDocumentIcon = (tipo) => {
    if (tipo === 'CPF') return <FaUser className="doc-icon cpf" />;
    if (tipo === 'CNPJ') return <FaBuilding className="doc-icon cnpj" />;
    if (tipo === 'OAB') return <MdGavel className="doc-icon oab" />;
    return null;
  };

  if (loading) {
    return (
      <div className="contacted-list-container">
        <div className="loading">Carregando...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="contacted-list-container">
        <div className="error">Erro: {error}</div>
      </div>
    );
  }

  return (
    <div className="contacted-list-container">
      <div className="list-header">
        <button className="back-button" onClick={() => navigate('/comercial/meus-precatorios')}>
          <MdArrowBack /> Voltar
        </button>
        <h1>Meus Precatórios - {stateNames[state] || state}</h1>
        <p className="list-subtitle">
          {hasActiveFilters
            ? `${filteredPrecatorios.length} de ${precatorios.length} precatórios (filtrado)`
            : `${precatorios.length} ${precatorios.length === 1 ? 'precatório' : 'precatórios'} na sua lista`
          }
        </p>
      </div>

      {/* Filter Bar */}
      {precatorios.length > 0 && (
        <div className="filter-section">
          <button
            className={`filter-toggle-btn ${showFilters ? 'active' : ''}`}
            onClick={() => setShowFilters(!showFilters)}
          >
            <MdFilterList /> Filtros {hasActiveFilters && `(${Object.values(filters).filter(v => v !== '').length})`}
          </button>

          {showFilters && (
            <div className="filter-bar">
              <div className="filter-grid">
                <div className="filter-item">
                  <label>Nome</label>
                  <input
                    type="text"
                    placeholder="Buscar por nome..."
                    value={filters.nome}
                    onChange={(e) => handleFilterChange('nome', e.target.value)}
                  />
                </div>

                <div className="filter-item">
                  <label>Nº Precatório</label>
                  <input
                    type="text"
                    placeholder="0000000-00..."
                    value={filters.numero_precatorio}
                    onChange={(e) => handleFilterChange('numero_precatorio', e.target.value)}
                  />
                </div>

                <div className="filter-item">
                  <label>CPF/OAB/CNPJ</label>
                  <input
                    type="text"
                    placeholder="Documento..."
                    value={filters.documento}
                    onChange={(e) => handleFilterChange('documento', e.target.value)}
                  />
                </div>

                <div className="filter-item filter-valor">
                  <label>Valor (R$)</label>
                  <div className="valor-inputs">
                    <input
                      type="number"
                      placeholder="Min"
                      value={filters.valorMin}
                      onChange={(e) => handleFilterChange('valorMin', e.target.value)}
                    />
                    <span>-</span>
                    <input
                      type="number"
                      placeholder="Max"
                      value={filters.valorMax}
                      onChange={(e) => handleFilterChange('valorMax', e.target.value)}
                    />
                  </div>
                </div>

                <div className="filter-item">
                  <label>Natureza</label>
                  <select
                    value={filters.natureza}
                    onChange={(e) => handleFilterChange('natureza', e.target.value)}
                  >
                    <option value="">Todas</option>
                    <option value="C">Comum</option>
                    <option value="A">Alimentar</option>
                    <option value="P">Prioritário</option>
                  </select>
                </div>

                <div className="filter-item">
                  <label>Ano Previsto</label>
                  <select
                    value={filters.anoPrevisto}
                    onChange={(e) => handleFilterChange('anoPrevisto', e.target.value)}
                  >
                    <option value="">Todos</option>
                    {anosPrevistos.map(ano => (
                      <option key={ano} value={ano}>{ano}</option>
                    ))}
                  </select>
                </div>
              </div>

              {hasActiveFilters && (
                <button className="clear-filters-btn" onClick={clearFilters}>
                  <MdClose /> Limpar Filtros
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {precatorios.length === 0 ? (
        <div className="empty-list">
          <p>Nenhum precatório adicionado ainda</p>
          <small>Use o botão "Pesquisar" na tela anterior para adicionar precatórios</small>
        </div>
      ) : filteredPrecatorios.length === 0 ? (
        <div className="empty-list">
          <p>Nenhum precatório encontrado com os filtros aplicados</p>
          <button className="clear-filters-link" onClick={clearFilters}>Limpar filtros</button>
        </div>
      ) : (
        <div className="precatorios-grid">
          {filteredPrecatorios.map((precatorio) => {
            const statusBadge = getStatusBadge(precatorio.status);
            
            return (
              <div key={precatorio._id} className="precatorio-card">
                <div className="card-header">
                  <div className="header-left">
                    {getDocumentIcon(precatorio.document_type_used)}
                    <div className="header-info">
                      <span className="doc-number">{formatDocument(precatorio.document_used, precatorio.document_type_used)}</span>
                      <span className="doc-name">{precatorio.nome}</span>
                    </div>
                    <button
                      className="edit-credor-button"
                      onClick={() => {
                        setEditingCredorPrecatorio(precatorio);
                        setShowEditCredorModal(true);
                      }}
                      title="Editar nome e documento do credor"
                    >
                      <MdEdit />
                    </button>
                  </div>
                  <span 
                    className="status-badge"
                    style={{ backgroundColor: statusBadge.color + '20', color: statusBadge.color }}
                  >
                    {statusBadge.label}
                  </span>
                </div>

                <div className="card-body">
                  <div className="info-grid">
                    <div className="info-item">
                      <span className="info-label">Precatório:</span>
                      <span className="info-value precatorio-number">{precatorio.numero_precatorio}</span>
                    </div>
                    {precatorio.natureza && (
                      <div className="info-item">
                        <span className="info-label">Natureza:</span>
                        <span className="info-value">{formatNatureza(precatorio.natureza)}</span>
                      </div>
                    )}
                    <div className="info-item">
                      <span className="info-label">Valor Atualizado:</span>
                      <span className="info-value value">{formatCurrency(precatorio.valor_atualizado)}</span>

                      {(() => {
                        const calculatedValue = calculateUpdatedValue(precatorio);
                        return calculatedValue ? (
                          <>
                            <span className="info-label" style={{ fontSize: '0.85em', marginTop: '4px' }}>
                              Valor Atualizado e Calculado:
                            </span>
                            <span className="info-value value" style={{ fontSize: '0.9em', opacity: 0.9 }}>
                              {formatCurrency(calculatedValue)}
                            </span>
                          </>
                        ) : null;
                      })()}

                      {(() => {
                        const naturezaUpper = precatorio.natureza?.toUpperCase();
                        const naturezaLower = precatorio.natureza?.toLowerCase();
                        const isIRRFApplicable = naturezaUpper === 'C' || naturezaLower === 'comum' ||
                                                 naturezaUpper === 'A' || naturezaLower === 'alimentar';

                        if (!isIRRFApplicable) {
                          return null;
                        }

                        const calculatedValue = calculateUpdatedValue(precatorio);
                        const baseValue = calculatedValue || precatorio.valor_atualizado;

                        if (!baseValue) return null;

                        return (
                          <>
                            <span className="info-label" style={{ fontSize: '0.85em', marginTop: '4px' }}>
                              Valor Líquido (27,5 % - IRRF):
                            </span>
                            <span className="info-value value" style={{ fontSize: '0.9em', opacity: 0.9 }}>
                              {formatCurrency(baseValue * 0.725)}
                            </span>
                          </>
                        );
                      })()}

                      {(() => {
                        const naturezaUpper = precatorio.natureza?.toUpperCase();
                        const naturezaLower = precatorio.natureza?.toLowerCase();
                        const isIRRFApplicable = naturezaUpper === 'C' || naturezaLower === 'comum' ||
                                                 naturezaUpper === 'A' || naturezaLower === 'alimentar';

                        if (!isIRRFApplicable) {
                          return null;
                        }

                        const calculatedValue = calculateUpdatedValue(precatorio);
                        const baseValue = calculatedValue || precatorio.valor_atualizado;
                        const valorLiquido = baseValue * 0.725;

                        const compoundFactor = calculateCompoundInterestFactor(precatorio.ano_pagamento_previsto);
                        if (!compoundFactor) return null;

                        const valorCompraMaximo = valorLiquido / (2 * compoundFactor);
                        const percentual = (valorCompraMaximo / valorLiquido) * 100;

                        return (
                          <>
                            <span className="info-label" style={{ fontSize: '0.85em', marginTop: '4px' }}>
                              Valor de Compra Máximo:
                            </span>
                            <span className="info-value value" style={{ fontSize: '0.9em', opacity: 0.9 }}>
                              {formatCurrency(valorCompraMaximo)} ({percentual.toFixed(1)}%)
                            </span>
                          </>
                        );
                      })()}
                    </div>
                    {precatorio.ano_pagamento_previsto && (
                      <div className="info-item">
                        <span className="info-label">Ano Previsto:</span>
                        <span className="info-value">{precatorio.ano_pagamento_previsto}</span>
                      </div>
                    )}
                    {precatorio.valor_negociado && (
                      <div className="info-item">
                        <span className="info-label">Valor Negociado:</span>
                        <span className="info-value value negotiated">{formatCurrency(precatorio.valor_negociado)}</span>
                      </div>
                    )}
                    {precatorio.valor_proposta && (
                      <div className="info-item">
                        <span className="info-label">Valor da Proposta:</span>
                        <span className="info-value value proposal">{formatCurrency(precatorio.valor_proposta)}</span>
                      </div>
                    )}
                    {precatorio.status === 'solicitacao_documentos' && (
                      <div className="info-item">
                        <span className="info-label">Checklist:</span>
                        <span className="info-value">
                          {precatorio.checklist_completed ? '✅ Completo' : '⏳ Pendente'}
                        </span>
                      </div>
                    )}
                    <div className="info-item">
                      <span className="info-label">Responsável:</span>
                      <span className="info-value">{precatorio.responsavel_atual?.name || '-'}</span>
                    </div>
                    <div className="info-item">
                      <span className="info-label">Última Atualização:</span>
                      <span className="info-value">{formatDate(precatorio.data_ultima_atualizacao)}</span>
                    </div>
                  </div>

                  <div className="contact-section">
                    <div className="contact-header">
                      <h4>Informações de Contato</h4>
                      <button 
                        className="edit-contact-button"
                        onClick={() => {
                          setSelectedPrecatorioForContact(precatorio);
                          setShowEditContactModal(true);
                        }}
                        title="Editar informações de contato"
                      >
                        <MdContactPhone />
                      </button>
                    </div>
                    
                    {precatorio.emails_contato && precatorio.emails_contato.length > 0 && (
                      <div className="contact-info">
                        <span className="contact-label">Emails:</span>
                        <span className="contact-value">{precatorio.emails_contato.join(', ')}</span>
                      </div>
                    )}

                    {precatorio.telefones_contato && precatorio.telefones_contato.length > 0 && (
                      <div className="contact-info">
                        <span className="contact-label">Telefones:</span>
                        <span className="contact-value">{precatorio.telefones_contato.join(', ')}</span>
                      </div>
                    )}
                    
                    {precatorio.address && (
                      <div className="contact-info">
                        <span className="contact-label">Endereço:</span>
                        <span className="contact-value">{precatorio.address}</span>
                      </div>
                    )}
                    
                    {precatorio.cidade && (
                      <div className="contact-info">
                        <span className="contact-label">Cidade:</span>
                        <span className="contact-value">{precatorio.cidade}{precatorio.uf ? ` - ${precatorio.uf}` : ''}</span>
                      </div>
                    )}
                    
                    {!precatorio.emails_contato?.length && !precatorio.telefones_contato?.length && !precatorio.address && (
                      <p className="no-contact-info">Nenhuma informação de contato disponível</p>
                    )}
                  </div>

                  {precatorio.comentarios && precatorio.comentarios.filter(c => !c.excluido).length > 0 && (
                    <div
                      className="comments-section clickable"
                      onClick={() => {
                        setSelectedPrecatorioComments(precatorio);
                        setShowAllCommentsModal(true);
                      }}
                    >
                      <span className="comments-label">Últimos comentários:</span>
                      <div className="comments-list">
                        {precatorio.comentarios.filter(c => !c.excluido).slice(-2).map((com, idx) => (
                          <div key={com._id || idx} className="comment-item">
                            <span className="comment-author">{com.usuario?.name || 'Sistema'}:</span>
                            <span className="comment-text">{com.texto}</span>
                            <span className="comment-date">
                              {formatDate(com.data)}
                              {com.editado && <span className="edited-indicator"> (editado)</span>}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <div className="card-actions">
                  <button 
                    className="action-button comment"
                    onClick={() => {
                      setSelectedPrecatorio(precatorio);
                      setShowCommentModal(true);
                    }}
                  >
                    <MdComment /> Comentar
                  </button>
                  
                  {/* Show proposal button for solicitacao_proposta status */}
                  {precatorio.status === 'solicitacao_proposta' && (
                    <button 
                      className="action-button proposal"
                      onClick={() => {
                        // Get fresh precatorio data from state
                        const currentPrecatorio = precatorios.find(p => p._id === precatorio._id);
                        setSelectedPrecatorio(currentPrecatorio);
                        setShowProposalModal(true);
                      }}
                    >
                      <MdAttachMoney /> Proposta
                    </button>
                  )}
                  
                  {/* Always show checklist button */}
                  <button 
                    className="action-button checklist"
                    onClick={() => {
                      // Get fresh precatorio data from state
                      const currentPrecatorio = precatorios.find(p => p._id === precatorio._id);
                      setSelectedPrecatorio(currentPrecatorio);
                      setShowChecklistModal(true);
                    }}
                  >
                    <MdGavel /> Documentos
                  </button>
                  
                  <button
                    className="action-button status"
                    onClick={() => {
                      setSelectedPrecatorio(precatorio);
                      setNewStatus(precatorio.status);
                      setShowStatusModal(true);
                    }}
                  >
                    <MdEdit /> Status
                  </button>

                  <button
                    className="action-button bad"
                    onClick={() => {
                      setSelectedPrecatorio(precatorio);
                      setShowMarkAsBadModal(true);
                    }}
                  >
                    <MdBlock /> Arquivar Prov.
                  </button>

                  <button
                    className="action-button audit"
                    onClick={() => {
                      setSelectedPrecatorioAuditLog(precatorio);
                      setShowAuditLogModal(true);
                    }}
                  >
                    <MdHistory /> Histórico
                  </button>

                  <button
                    className="action-button juris"
                    onClick={() => handleOpenJurisConsult(precatorio)}
                  >
                    <MdStorage /> Data Juris
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* All Comments Modal */}
      {showAllCommentsModal && selectedPrecatorioComments && (
        <div className="modal-overlay" onClick={() => {
          setShowAllCommentsModal(false);
          setEditingComment(null);
          setEditCommentText('');
        }}>
          <div className="modal-content comments-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Todos os Comentários</h3>
            <div className="all-comments-list">
              {selectedPrecatorioComments.comentarios && selectedPrecatorioComments.comentarios.length > 0 ? (
                selectedPrecatorioComments.comentarios.map((com, idx) => (
                  <div
                    key={com._id || idx}
                    className={`comment-item ${com.excluido ? 'deleted' : ''} ${editingComment?._id === com._id ? 'editing' : ''}`}
                  >
                    {editingComment?._id === com._id ? (
                      // Edit mode
                      <div className="comment-edit-form">
                        <textarea
                          value={editCommentText}
                          onChange={(e) => setEditCommentText(e.target.value)}
                          rows={3}
                          autoFocus
                        />
                        <div className="edit-actions">
                          <button
                            className="cancel-btn"
                            onClick={() => {
                              setEditingComment(null);
                              setEditCommentText('');
                            }}
                          >
                            Cancelar
                          </button>
                          <button
                            className="save-btn"
                            onClick={handleEditComment}
                            disabled={!editCommentText.trim()}
                          >
                            Salvar
                          </button>
                        </div>
                      </div>
                    ) : (
                      // View mode
                      <>
                        <div className="comment-header">
                          <span className="comment-author">{com.usuario?.name || 'Sistema'}:</span>
                          <span className="comment-date">{formatDate(com.data)}</span>
                          {formatCommentStatus(com) && (
                            <span className="comment-status">{formatCommentStatus(com)}</span>
                          )}
                        </div>

                        {com.excluido ? (
                          <span className="comment-text deleted-text">[Comentário excluído]</span>
                        ) : (
                          <span className="comment-text">{com.texto}</span>
                        )}

                        {/* Action buttons - only show if user can modify and not in edit mode */}
                        {canModifyComment(com) && !editingComment && (
                          <div className="comment-actions">
                            <button
                              className="action-icon edit"
                              onClick={() => {
                                setEditingComment(com);
                                setEditCommentText(com.texto);
                              }}
                              title="Editar comentário"
                            >
                              <MdEdit />
                            </button>
                            <button
                              className="action-icon delete"
                              onClick={() => {
                                setCommentToDelete(com);
                                setShowDeleteConfirm(true);
                              }}
                              title="Excluir comentário"
                            >
                              <MdDelete />
                            </button>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                ))
              ) : (
                <p className="no-comments">Nenhum comentário disponível</p>
              )}
            </div>

            {/* Add Comment Section */}
            <div className="add-comment-section">
              <textarea
                value={newCommentInModal}
                onChange={(e) => setNewCommentInModal(e.target.value)}
                placeholder="Digite seu comentário..."
                rows={3}
              />
              <button
                className="add-comment-btn"
                onClick={handleAddCommentFromModal}
                disabled={!newCommentInModal.trim()}
              >
                Adicionar Comentário
              </button>
            </div>

            <div className="modal-actions">
              <button onClick={() => {
                setShowAllCommentsModal(false);
                setNewCommentInModal('');
                setEditingComment(null);
                setEditCommentText('');
              }}>Fechar</button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Comment Confirmation Modal */}
      {showDeleteConfirm && commentToDelete && (
        <div className="modal-overlay" onClick={() => {
          setShowDeleteConfirm(false);
          setCommentToDelete(null);
        }}>
          <div className="modal-content confirm-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Confirmar Exclusão</h3>
            <p>Tem certeza que deseja excluir este comentário?</p>
            <div className="comment-preview">
              <span className="comment-author">{commentToDelete.usuario?.name}:</span>
              <span className="comment-text">{commentToDelete.texto}</span>
            </div>
            <div className="modal-actions">
              <button onClick={() => {
                setShowDeleteConfirm(false);
                setCommentToDelete(null);
              }}>Cancelar</button>
              <button onClick={handleDeleteComment} className="danger">Excluir</button>
            </div>
          </div>
        </div>
      )}

      {/* Comment Modal */}
      {showCommentModal && (
        <div className="modal-overlay" onClick={() => setShowCommentModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Adicionar Comentário</h3>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Digite seu comentário..."
              rows={4}
              autoFocus
            />
            <div className="modal-actions">
              <button onClick={() => setShowCommentModal(false)}>Cancelar</button>
              <button onClick={handleAddComment} className="primary">Adicionar</button>
            </div>
          </div>
        </div>
      )}

      {/* Status Modal */}
      {showStatusModal && (
        <div className="modal-overlay" onClick={() => setShowStatusModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Atualizar Status</h3>
            <select value={newStatus} onChange={(e) => setNewStatus(e.target.value)}>
              {statusOptions.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
            <textarea
              value={statusMotivo}
              onChange={(e) => setStatusMotivo(e.target.value)}
              placeholder="Motivo da mudança (opcional)..."
              rows={3}
            />
            <div className="modal-actions">
              <button onClick={() => setShowStatusModal(false)}>Cancelar</button>
              <button onClick={handleUpdateStatus} className="primary">Atualizar</button>
            </div>
          </div>
        </div>
      )}
      
      {/* Edit Contact Modal */}
      <EditContactModal
        isOpen={showEditContactModal}
        onClose={() => {
          setShowEditContactModal(false);
          setSelectedPrecatorioForContact(null);
        }}
        precatorio={selectedPrecatorioForContact}
        state={state}
        onSuccess={fetchPrecatorios}
      />

      {/* Document Checklist Modal */}
      {showChecklistModal && selectedPrecatorio && (
        <DocumentChecklistModal
          isOpen={showChecklistModal}
          onClose={() => {
            setShowChecklistModal(false);
            setSelectedPrecatorio(null);
          }}
          precatorio={selectedPrecatorio}
          state={state}
          onUpdate={(updatedPrecatorio) => {
            setPrecatorios(prev => 
              prev.map(p => p._id === updatedPrecatorio._id ? updatedPrecatorio : p)
            );
            setShowChecklistModal(false);
            setSelectedPrecatorio(null);
          }}
        />
      )}

      {/* Proposal Modal */}
      {showProposalModal && selectedPrecatorio && (
        <ProposalModal
          isOpen={showProposalModal}
          onClose={() => {
            setShowProposalModal(false);
            setSelectedPrecatorio(null);
          }}
          precatorio={selectedPrecatorio}
          state={state}
          onUpdate={(updatedPrecatorio) => {
            setPrecatorios(prev =>
              prev.map(p => p._id === updatedPrecatorio._id ? updatedPrecatorio : p)
            );
            setShowProposalModal(false);
            setSelectedPrecatorio(null);
          }}
        />
      )}

      {/* Mark as Bad Modal */}
      {showMarkAsBadModal && selectedPrecatorio && (
        <div className="modal-overlay" onClick={() => {
          setShowMarkAsBadModal(false);
          setBadMotivo('');
          setBadMotivoDescricao('');
          setSelectedPrecatorio(null);
        }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Arquivar Provisoriamente</h3>
            <p className="modal-warning">
              Este precatório será arquivado provisoriamente e removido da sua lista ativa.
            </p>
            <label>Motivo:</label>
            <select
              value={badMotivo}
              onChange={(e) => setBadMotivo(e.target.value)}
            >
              <option value="">Selecione o motivo</option>
              {badMotivoOptions.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
            <label>Comentário (obrigatório):</label>
            <textarea
              value={badMotivoDescricao}
              onChange={(e) => setBadMotivoDescricao(e.target.value)}
              placeholder="Descreva o motivo do arquivamento provisório..."
              rows={3}
            />
            <div className="modal-actions">
              <button onClick={() => {
                setShowMarkAsBadModal(false);
                setBadMotivo('');
                setBadMotivoDescricao('');
                setSelectedPrecatorio(null);
              }}>Cancelar</button>
              <button
                onClick={handleMarkAsBad}
                className="danger"
                disabled={!badMotivo || !badMotivoDescricao.trim()}
              >
                Arquivar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Credor Modal */}
      <EditCredorModal
        isOpen={showEditCredorModal}
        onClose={() => {
          setShowEditCredorModal(false);
          setEditingCredorPrecatorio(null);
        }}
        precatorio={editingCredorPrecatorio}
        state={state}
        onSuccess={fetchPrecatorios}
      />

      {/* Audit Log Modal */}
      {showAuditLogModal && selectedPrecatorioAuditLog && (
        <div className="modal-overlay" onClick={() => {
          setShowAuditLogModal(false);
          setSelectedPrecatorioAuditLog(null);
        }}>
          <div className="modal-content audit-log-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Histórico de Alterações</h3>
            <div className="audit-precatorio-info">
              <span>Precatório: {selectedPrecatorioAuditLog.numero_precatorio}</span>
            </div>
            
            {selectedPrecatorioAuditLog.historico_alteracoes && selectedPrecatorioAuditLog.historico_alteracoes.length > 0 ? (
              <div className="audit-log-list">
                {selectedPrecatorioAuditLog.historico_alteracoes
                  .slice()
                  .sort((a, b) => new Date(b.data) - new Date(a.data))
                  .map((entry, idx) => (
                  <div key={idx} className="audit-log-entry">
                    <div className="audit-log-header">
                      <span className="audit-date">{formatDate(entry.data)}</span>
                      <span className="audit-user">{entry.usuario?.name || 'Sistema'}</span>
                    </div>
                    <div className="audit-log-change">
                      <span className="audit-field">
                        {entry.campo === 'nome' ? 'Nome' : 
                         entry.campo === 'document_used' ? 'Documento' : 
                         entry.campo}
                      </span>
                      <div className="audit-values">
                        <span className="audit-old">{entry.valor_anterior || '(vazio)'}</span>
                        <span className="audit-arrow">→</span>
                        <span className="audit-new">{entry.valor_novo || '(vazio)'}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="no-audit-entries">Nenhuma alteração registrada</p>
            )}
            
            <div className="modal-actions">
              <button onClick={() => {
                setShowAuditLogModal(false);
                setSelectedPrecatorioAuditLog(null);
              }}>Fechar</button>
            </div>
          </div>
        </div>
      )}

      {/* JurisConsult Modal */}
      <JurisConsultModal
        isOpen={showJurisModal}
        onClose={() => {
          setShowJurisModal(false);
          setSelectedPrecatorioJuris(null);
          setJurisConsultData(null);
        }}
        data={jurisConsultData}
        numeroPrecatorio={selectedPrecatorioJuris?.numero_precatorio}
        loading={loadingJuris}
      />
    </div>
  );
};

export default ContactedPrecatoriosList;